import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, getToken } from "../../../software/api";
import { toast } from "sonner";
import { Palette, ImagePlus, Check, X, Loader2, Plus, Trash2, ShieldCheck, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SwatchTile({ c, onChange, onDelete, isAdmin }) {
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ name: c.name, item: c.item || "", microban: !!c.microban });
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("That's not an image file."); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/uploads`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Upload failed");
      const url = `${process.env.REACT_APP_BACKEND_URL}${d.url}`;
      const updated = await api("PATCH", `/colours/${c.id}/image`, { image_url: url });
      onChange(updated); toast.success(`Photo added to ${c.name}`);
    } catch (err) { toast.error(err.message || "Upload failed"); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!f.name.trim()) { toast.error("Colour name is required"); return; }
    const updated = await api("PATCH", `/colours/${c.id}`, { name: f.name.trim(), item: f.item.trim(), microban: f.microban, image_url: c.image_url, notes: c.notes || "", active: c.active !== false });
    onChange(updated); setEdit(false); toast.success("Colour saved");
  };

  return (
    <div data-testid={`colour-tile-${c.id}`} className="rounded-2xl border-2 border-slate-200 bg-white overflow-hidden">
      <div onClick={() => inputRef.current?.click()} className="aspect-square bg-slate-50 flex items-center justify-center relative cursor-pointer group">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={`colour-file-${c.id}`} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        {c.image_url ? (
          <>
            <img src={c.image_url} alt={c.name} className="h-full w-full object-cover" />
            <span className="absolute top-1.5 left-1.5 h-6 w-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow"><Check className="h-3.5 w-3.5" /></span>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1 text-slate-400 px-2 text-center">
            {busy ? <Loader2 className="h-7 w-7 animate-spin text-sky-500" /> : <ImagePlus className="h-7 w-7" />}
            <span className="text-[11px] font-semibold">{busy ? "Uploading…" : "Tap to add swatch"}</span>
          </div>
        )}
        {busy && c.image_url && <div className="absolute inset-0 bg-white/60 flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-sky-500" /></div>}
      </div>
      <div className="p-2.5">
        {edit ? (
          <div className="space-y-1.5">
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`colour-edit-name-${c.id}`} placeholder="Colour name" className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
            <input value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} data-testid={`colour-edit-item-${c.id}`} placeholder="Applies to (Cabinet / Shell)" className="w-full rounded border border-slate-300 px-2 py-1 text-xs" />
            <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={f.microban} onChange={(e) => setF({ ...f, microban: e.target.checked })} className="h-3.5 w-3.5" /> Microban</label>
            <div className="flex gap-1.5">
              <button onClick={saveEdit} data-testid={`colour-save-${c.id}`} className="flex-1 text-xs font-bold bg-emerald-600 text-white py-1 rounded hover:bg-emerald-700">Save</button>
              <button onClick={() => setEdit(false)} className="text-xs font-bold text-slate-500 px-2 py-1 rounded border border-slate-300">Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <p className="font-semibold text-sm text-slate-800 truncate flex items-center gap-1" title={c.name}>{c.name}{c.microban && <span title="Microban" className="text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" /></span>}</p>
            <p className="text-[11px] text-slate-400 truncate">{c.item || "—"}</p>
            <div className="flex gap-2 mt-1">
              <button onClick={() => setEdit(true)} data-testid={`colour-editbtn-${c.id}`} className="text-[11px] font-bold text-sky-700 hover:underline">Rename</button>
              {isAdmin && <button onClick={() => { if (window.confirm(`Delete colour "${c.name}"?`)) onDelete(c.id); }} data-testid={`colour-del-${c.id}`} className="text-[11px] font-bold text-rose-600 hover:underline">Delete</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Colours({ isAdmin, onExit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [nf, setNf] = useState({ name: "", item: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await api("GET", "/colours")); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onChange = (updated) => setItems((xs) => xs.map((c) => c.id === updated.id ? updated : c));
  const onDelete = async (id) => { await api("DELETE", `/colours/${id}`); setItems((xs) => xs.filter((c) => c.id !== id)); toast.success("Colour deleted"); };
  const add = async () => {
    if (!nf.name.trim()) { toast.error("Colour name is required"); return; }
    const c = await api("POST", "/colours", { name: nf.name.trim(), item: nf.item.trim(), microban: false, image_url: "", notes: "", active: true });
    setItems((xs) => [...xs, c]); setNf({ name: "", item: "" }); setAdding(false); toast.success(`Colour "${c.name}" added`);
  };

  return (
    <div data-testid="colours-panel" className="space-y-5">
      {onExit && <button onClick={onExit} data-testid="colours-switchboard" className="inline-flex items-center gap-1.5 rounded-lg border-2 border-[#123a63] bg-white px-4 py-2 text-sm font-black text-[#123a63] hover:bg-slate-50"><ArrowLeft className="h-4 w-4" /> Switch Menu</button>}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 mb-1 flex items-center gap-2"><Palette className="h-6 w-6 text-sky-600" /> Colour Swatches</h1>
          <p className="text-sm text-slate-500">Shell &amp; cabinet colours for spa quotes. Tap a tile to add its swatch photo.</p>
        </div>
        <button onClick={() => setAdding(true)} data-testid="colour-add-btn" className="inline-flex items-center gap-1.5 rounded-xl bg-[#123a63] text-white font-bold text-sm px-4 py-2.5 hover:bg-[#1a4f8a]"><Plus className="h-4 w-4" /> Add colour</button>
      </div>

      {adding && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3" data-testid="colour-add-form">
          <label className="text-sm text-slate-600">Colour name<input autoFocus value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} data-testid="colour-new-name" className="block mt-1 rounded-lg border border-slate-300 px-3 py-2 w-52" /></label>
          <label className="text-sm text-slate-600">Applies to<input value={nf.item} onChange={(e) => setNf({ ...nf, item: e.target.value })} data-testid="colour-new-item" placeholder="Cabinet / Shell" className="block mt-1 rounded-lg border border-slate-300 px-3 py-2 w-44" /></label>
          <button onClick={add} data-testid="colour-new-save" className="rounded-xl bg-emerald-600 text-white font-bold text-sm px-4 py-2.5 hover:bg-emerald-700">Save</button>
          <button onClick={() => setAdding(false)} className="rounded-xl border border-slate-300 text-slate-600 font-bold text-sm px-4 py-2.5">Cancel</button>
        </div>
      )}

      {loading ? <p className="text-slate-500 py-10 text-center">Loading colours…</p> : items.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 p-10 text-center" data-testid="colours-empty">
          <Palette className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500 font-semibold">No colours yet.</p>
          <p className="text-sm text-slate-400 mt-1">Tap "Add colour" to create your first shell/cabinet swatch.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {items.map((c) => <SwatchTile key={c.id} c={c} onChange={onChange} onDelete={onDelete} isAdmin={isAdmin} />)}
        </div>
      )}
    </div>
  );
}
